// Zadanie otwarte E8: skala i stosunek pól.
// Mała tablica kwadratowa narysowana w skali 1:k ma bok s cm (na rysunku).
// Duża tablica prostokątna ma wymiary rzeczywiste a × b cm.
// Pytanie: ile razy pole dużej jest większe od pola małej?

import React from "react";
import { BlockMath, InlineMath } from "react-katex";
import FractionInput from "../../../components/inputs/FractionInput";
import { scoreStaged } from "../../openTaskScoring";

function buildData({ drawingSide, scale, largeDimA, largeDimB }) {
  const realSmallSide = drawingSide * scale;
  const areaSmall = realSmallSide * realSmallSide;
  const areaLarge = largeDimA * largeDimB;
  const ratio = areaLarge / areaSmall;
  return {
    drawingSide,
    scale,
    largeDimA,
    largeDimB,
    realSmallSide,
    areaSmall,
    areaLarge,
    ratio,
    maxPoints: 2,
  };
}

function createOriginalTask() {
  return {
    type: "E8ScaleAreaRatioOpenTask",
    data: buildData({ drawingSide: 3, scale: 20, largeDimA: 240, largeDimB: 90 }),
    source: {
      variant: "original",
      exam: "E8",
      session: "2025_MAJ",
      taskNumber: 19,
    },
    tags: ["e8", "planimetria", "skala", "pole", "stosunek-pol", "zadanie-otwarte"],
  };
}

function pick(arr) {
  return arr[Math.floor(Math.random() * arr.length)];
}

const DRAWING_SIDES = [2, 3, 4, 5, 6];
const SCALES = [10, 20, 25, 50];
const RECTANGLE_FACTOR_PAIRS = [
  [2, 1],
  [3, 1],
  [4, 1],
  [2, 2],
  [5, 1],
  [6, 1],
  [3, 2],
  [4, 2],
];

function createRandomData() {
  const drawingSide = pick(DRAWING_SIDES);
  const scale = pick(SCALES);
  const [widthFactor, heightFactor] = pick(RECTANGLE_FACTOR_PAIRS);
  const realSmallSide = drawingSide * scale;

  return buildData({
    drawingSide,
    scale,
    largeDimA: realSmallSide * widthFactor,
    largeDimB: realSmallSide * heightFactor,
  });
}

export function generateTask(generationOptions = {}) {
  if (generationOptions.variant === "original") return createOriginalTask();
  return {
    type: "E8ScaleAreaRatioOpenTask",
    data: createRandomData(),
    source: { variant: "random", basedOn: "E8_2025_MAJ_19" },
    tags: ["e8", "planimetria", "skala", "pole", "stosunek-pol", "zadanie-otwarte"],
  };
}

function parseNum(value) {
  if (value == null) return null;
  const raw = typeof value === "string" ? value : (value.raw ?? String(value));
  const n = Number(String(raw).trim().replace(",", "."));
  return Number.isFinite(n) ? n : null;
}

export function renderTask({ data }) {
  const { drawingSide, scale, largeDimA, largeDimB } = data;
  return (
    <div style={{ textAlign: "left", lineHeight: 1.7 }}>
      <p>
        Na ścianie wiszą dwie tablice: mała kwadratowa i duża prostokątna. Mała
        tablica narysowana w skali{" "}
        <InlineMath math={`1\\!:\\!${scale}`} /> jest kwadratem o boku{" "}
        <InlineMath math={`${drawingSide}\\text{ cm}`} />. Rzeczywiste wymiary
        dużej prostokątnej tablicy są równe{" "}
        <InlineMath math={`${largeDimA}\\text{ cm}`} /> i{" "}
        <InlineMath math={`${largeDimB}\\text{ cm}`} />.
      </p>
      <p>
        <strong>
          Oblicz, ile razy pole dużej tablicy jest większe od pola małej
          tablicy. Zapisz obliczenia.
        </strong>
      </p>
    </div>
  );
}

export function renderInput({ data }, userAnswer = {}, onChange, disabled) {
  const set = (field) => (val) => onChange({ ...(userAnswer || {}), [field]: val });
  return (
    <div style={{ display: "grid", gap: 16, textAlign: "left", marginTop: 8 }}>
      <div
        style={{
          fontSize: 12.5,
          opacity: 0.78,
          lineHeight: 1.45,
          borderLeft: "3px solid var(--color-accent)",
          paddingLeft: 8,
        }}
      >
        <strong>{"Wynik ko\u0144cowy i wynik po\u015bredni."}</strong>{" "}
        {"Poprawny stosunek p\u00f3l daje pe\u0142ne 2 punkty. Rzeczywisty bok ma\u0142ej tablicy mo\u017ce da\u0107 punkt cz\u0105stkowy."}
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        <span style={{ opacity: 0.88 }}>
          Rzeczywisty bok małej tablicy (w cm):
        </span>
        <FractionInput
          value={userAnswer.side}
          onChange={set("side")}
          disabled={disabled}
          label=""
          suffixLatex={"\\text{ cm}"}
          placeholder="np. 60"
        />
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        <span style={{ opacity: 0.88 }}>
          Pole dużej tablicy jest{" "}
          <strong style={{ color: "var(--color-accent)" }}>___</strong> razy
          większe od pola małej:
        </span>
        <FractionInput
          value={userAnswer.ratio}
          onChange={set("ratio")}
          disabled={disabled}
          label=""
          placeholder="np. 6"
        />
      </div>
    </div>
  );
}

export function checkAnswer({ data }, userAnswer = {}) {
  const sideOk  = parseNum(userAnswer.side)  === data.realSmallSide;
  const ratioOk = parseNum(userAnswer.ratio) === data.ratio;
  return scoreStaged({
    resultOk: ratioOk,
    milestones: [sideOk],
    maxScore: data.maxPoints,
    parts: {
      "bok małej tablicy": sideOk,
      "ile razy większe pole": ratioOk,
    },
  });
}

export function renderAnswer({ data }) {
  return (
    <div style={{ textAlign: "left", lineHeight: 1.7 }}>
      <div>{"Przyk\u0142adowa poprawna odpowied\u017a:"}</div>
      <BlockMath math={`${data.drawingSide}\\text{ cm}\\cdot ${data.scale}=${data.realSmallSide}\\text{ cm}`} />
      <BlockMath math={`P_{\\text{ma\u0142a}}=${data.realSmallSide}^2=${data.areaSmall}\\text{ cm}^2`} />
      <BlockMath math={`P_{\\text{du\u017ca}}=${data.largeDimA}\\cdot ${data.largeDimB}=${data.areaLarge}\\text{ cm}^2`} />
      <BlockMath math={`\\frac{${data.areaLarge}}{${data.areaSmall}}=${data.ratio}`} />
      <div>{"Odpowied\u017a: bok ma\u0142ej tablicy ma "}<InlineMath math={`${data.realSmallSide}\\text{ cm}`} />{", a pole du\u017cej tablicy jest "}<InlineMath math={`${data.ratio}`} />{" razy wi\u0119ksze."}</div>
    </div>
  );
}

export function renderUserAnswer(userAnswer = {}) {
  const fmt = (v) => {
    const n = parseNum(v);
    return n == null ? <i>(brak)</i> : <InlineMath math={`${n}`} />;
  };
  return (
    <div style={{ textAlign: "left", display: "grid", gap: 4 }}>
      <div>Bok małej tablicy: {fmt(userAnswer.side)} cm</div>
      <div>Ile razy większe: {fmt(userAnswer.ratio)}</div>
    </div>
  );
}

export function renderExplanation({ data }) {
  const { drawingSide, scale, largeDimA, largeDimB, realSmallSide, areaSmall, areaLarge, ratio } = data;
  return (
    <div style={{ textAlign: "left", lineHeight: 1.75 }}>
      <p>
        <strong>Krok 1.</strong> Wyznaczamy rzeczywisty bok małej tablicy ze
        skali <InlineMath math={`1\\!:\\!${scale}`} />.
      </p>
      <BlockMath math={`${drawingSide}\\text{ cm} \\times ${scale} = ${realSmallSide}\\text{ cm}`} />
      <p>
        <strong>Krok 2.</strong> Obliczamy pole małej kwadratowej tablicy.
      </p>
      <BlockMath math={`P_{\\text{mała}} = ${realSmallSide}^2 = ${areaSmall}\\text{ cm}^2`} />
      <p>
        <strong>Krok 3.</strong> Obliczamy pole dużej prostokątnej tablicy.
      </p>
      <BlockMath math={`P_{\\text{duża}} = ${largeDimA} \\times ${largeDimB} = ${areaLarge}\\text{ cm}^2`} />
      <p>
        <strong>Krok 4.</strong> Obliczamy, ile razy pole dużej tablicy jest
        większe od pola małej.
      </p>
      <BlockMath
        math={`\\frac{P_{\\text{duża}}}{P_{\\text{mała}}} = \\frac{${areaLarge}}{${areaSmall}} = ${ratio}`}
      />
      <p>
        Pole dużej tablicy jest <InlineMath math={`${ratio}`} /> razy większe
        od pola małej tablicy.
      </p>
    </div>
  );
}
